import { List } from "@/components/List";
import { event, StructureEvents } from "@/endpoints/dryp/event";
import { useAsyncStateStore } from "@/hooks/useAsyncStateStore";
import { useServerTable } from "@/hooks/useServerTable";
import { mockServerTableQuery, QueryParams } from "@/utils/mockServerTableQuery";
import { observer } from "mobx-react-lite";
import { Columns } from "./Columns";
import { EventsList } from "./EventsList";
import { getEventExportData } from "./EventColumns";
import { DateRange } from "react-day-picker";
import { useState } from "react";
import { DateRangePicker } from "@/components/DateRangePicker";
import { DateTime } from "luxon";

const today = DateTime.now().startOf("day");
const sevenDaysAgo = today.minus({ days: 6 });

const initialDateRange = {
	from: sevenDaysAgo.toJSDate(),
	to: today.toJSDate(),
};

const isSameRange = (range1?: DateRange, range2?: DateRange): boolean => {
	if (!range1?.from || !range1?.to || !range2?.from || !range2?.to) return false;

	const from1 = DateTime.fromJSDate(range1.from).startOf("day");
	const to1 = DateTime.fromJSDate(range1.to).startOf("day");

	const from2 = DateTime.fromJSDate(range2.from).startOf("day");
	const to2 = DateTime.fromJSDate(range2.to).startOf("day");

	return from1.equals(from2) && to1.equals(to2);
};

let preFetchedData: StructureEvents[] = [];
let selectedDateRange: DateRange = initialDateRange;

const fetchEvents = async (params: QueryParams, dateRange: DateRange) => {
	const response =
		preFetchedData?.length > 0 && isSameRange(selectedDateRange, dateRange)
			? preFetchedData
			: await event.getAllEvents({
					to: dateRange.to?.toISOString() || "",
					from: dateRange.from?.toISOString() || "",
				});
	const data = mockServerTableQuery({
		data: response || [],
		page: params.page,
		pageSize: params.pageSize,
		filters: params.filters,
		sort: params.sort,
	});
	preFetchedData = response;
	selectedDateRange = dateRange;
	return data;
};

export const Overflow = observer(() => {
	const [dateRange, setDateRange] = useState<DateRange | undefined>(initialDateRange);

	const eventsApi = useAsyncStateStore(fetchEvents);
	const events = eventsApi.data?.queryData || [];

	const { table, tableState } = useServerTable({
		columns: Columns,
		data: events,
		fetchFn: params => eventsApi.fetch(params, dateRange || initialDateRange),
		initialValues: {
			page: 0,
			pageSize: 10,
		},
	});

	const onDateSelected = (dateRange: DateRange) => {
		if (!isSameRange(selectedDateRange, dateRange)) {
			eventsApi.fetch({ page: tableState.page, pageSize: tableState.pageSize }, dateRange);
		}
	};

	return (
		<div className="flex flex-col gap-4 md:gap-6 p-4 md:p-6 overflow-hidden">
			<header className="flex items-center justify-between">
				<h1 className="text-2xl md:text-4xl font-semibold tracking-tight">Overflow</h1>
			</header>

			<List
				table={table}
				pagination={{
					total: eventsApi.data?.pagination.total || 0,
				}}
				toolbar={{
					expandedRowPanelExport: row => {
						const events = row.original.events ?? [];
						return getEventExportData(events);
					},
					customFilter: (
						<div className="relative z-10">
							<DateRangePicker date={dateRange} onClose={onDateSelected} onChange={setDateRange} />
						</div>
					),
				}}
				layout={{
					loading: eventsApi.loading,
					rowClassName: "bg-blue-100 cursor-pointer",
					tableClassName: "border-separate border-spacing-y-2 border-spacing-x-0",
					tableWrapperClassName: "border-none",
					spinnerText: ["Loading"],
				}}
				expandedRowPanel={row => <EventsList row={row} />}
			/>
		</div>
	);
});
